import React, { Fragment, useState } from "react";
import Multiselect from "multiselect-react-dropdown";
import { TimeSlot } from "./keyHostListing";
import CountrySelect from "../../utils/countryCode";
import en from "react-phone-number-input/locale/en.json";
import SortedCountryDropdown from "../../utils/sortedCountryDropdown";
import { toast } from "react-toastify";

type Props = {
  values: any;
  setFieldValue: any;
  handleChange: (e: any) => void;
  handleBlur: any;
  touched: any;
  errors: any;
  country: any;
  handleCountryChange: (e: any) => void;
  handelPhoneNumber: (e: any) => void;
  timeSlots: any;
  setTimeSlots: any;
  updateTimeSlot: any;
  handleSubmit: any;
  BoxItems: any;
  selectedOptions: any;
  onSelected: any;
  isOpen: boolean | any;
  handleModalClose: () => void;
  modalTitle: string;
  onRemove: any;
  handleErrorClick: () => void;
  errorMessage: string;
  timeError: boolean;
};

const KeyHostModal = ({
  setFieldValue,
  values,
  handleChange,
  handleBlur,
  errors,
  touched,
  country,
  handleCountryChange,
  handelPhoneNumber: _handelPhoneNumber,
  timeSlots,
  setTimeSlots,
  handleSubmit,
  updateTimeSlot,
  BoxItems,
  selectedOptions,
  onSelected,
  onRemove,
  isOpen,
  handleModalClose,
  modalTitle,
  handleErrorClick,
  errorMessage,
  timeError,
}: Props) => {
  const [isGeocoding, setIsGeocoding] = useState(false);
  const [bulkStart, setBulkStart] = useState("09:00");
  const [bulkEnd, setBulkEnd] = useState("17:00");

  // Apply a named schedule preset to all time slots at once
  const applyPreset = (preset: string) => {
    const newSlots = timeSlots.map((slot: TimeSlot, index: number) => {
      switch (preset) {
        case "business":
          // Mon–Fri open 09:00–17:00, Sat–Sun closed
          return index < 5
            ? { ...slot, start_time: "09:00", end_time: "17:00", closed: false }
            : { ...slot, start_time: "", end_time: "", closed: true };
        case "extended":
          // Mon–Sat open 09:00–18:00, Sun closed
          return index < 6
            ? { ...slot, start_time: "09:00", end_time: "18:00", closed: false }
            : { ...slot, start_time: "", end_time: "", closed: true };
        case "7days":
          return { ...slot, start_time: "09:00", end_time: "17:00", closed: false };
        case "247":
          return { ...slot, start_time: "00:00", end_time: "23:59", closed: false };
        default:
          return slot;
      }
    });
    setTimeSlots(newSlots);
    toast.success("Preset applied!");
  };

  // Push bulkStart/bulkEnd to the specified day indices, marking them open
  const applyBulkTime = (indices: number[]) => {
    if (!bulkStart || !bulkEnd) {
      toast.warning("Please set both an open and close time before applying.");
      return;
    }
    const newSlots = [...timeSlots];
    indices.forEach((i) => {
      newSlots[i] = { ...newSlots[i], start_time: bulkStart, end_time: bulkEnd, closed: false };
    });
    setTimeSlots(newSlots);
    toast.success("Hours applied!");
  };

  // Copy one row's hours to every other currently-open day
  const copyRowToAll = (sourceIndex: number) => {
    const source = timeSlots[sourceIndex];
    if (!source.start_time || !source.end_time) {
      toast.warning("Please set times for this day first.");
      return;
    }
    const newSlots = timeSlots.map((slot: TimeSlot, idx: number) => {
      if (idx === sourceIndex || slot.closed) return slot;
      return { ...slot, start_time: source.start_time, end_time: source.end_time };
    });
    setTimeSlots(newSlots);
    toast.success(`${source.day}'s hours copied to all open days.`);
  };

  const handleselectCountry = (val: any) => {
    setFieldValue("country", val);
  };

  // Resolve country name to ISO 3166-1 alpha-2 code for Nominatim
  const getCountryCode = (countryValue: string): string | null => {
    if (!countryValue) return null;
    // If it's already a 2-letter code
    if (/^[A-Z]{2}$/.test(countryValue)) return countryValue.toLowerCase();
    // Try to look up by name using the already-imported `en` locale map
    try {
      const countryNames = en as Record<string, string>;
      const code = Object.keys(countryNames).find(
        (key) => countryNames[key].toLowerCase() === countryValue.toLowerCase()
      );
      if (code) {
        return code.toLowerCase();
      }
    } catch (_) {}
    return null;
  };

  // Extract UK postcode from an address string (e.g. "NW9 4BP")
  const extractUKPostcode = (address: string): string | null => {
    const match = address.match(
      /\b([A-Z]{1,2}\d{1,2}[A-Z]?\s*\d[A-Z]{2})\b/i
    );
    return match ? match[1].trim() : null;
  };

  // Try Nominatim geocoding service
  const tryNominatim = async (
    address: string,
    countryCode: string | null
  ): Promise<{ lat: string; lon: string } | null> => {
    const baseUrl = "https://nominatim.openstreetmap.org/search";
    const commonParams = "format=json&limit=5&addressdetails=1";
    const queries: string[] = [];

    if (countryCode) {
      queries.push(
        `${baseUrl}?${commonParams}&countrycodes=${countryCode}&q=${encodeURIComponent(address)}`
      );
    }
    if (values.country) {
      queries.push(
        `${baseUrl}?${commonParams}&q=${encodeURIComponent(`${address}, ${values.country}`)}`
      );
    }
    queries.push(`${baseUrl}?${commonParams}&q=${encodeURIComponent(address)}`);

    for (const url of queries) {
      try {
        const response = await fetch(url, { headers: { "Accept-Language": "en" } });
        if (!response.ok) continue;
        const data = await response.json();
        if (data && data.length > 0) {
          return { lat: data[0].lat, lon: data[0].lon };
        }
      } catch (_) {}
    }
    return null;
  };

  // Try Photon (Komoot) geocoding service — more reliable for UK addresses
  const tryPhoton = async (
    address: string,
    countryCode: string | null
  ): Promise<{ lat: string; lon: string } | null> => {
    try {
      let url = `https://photon.komoot.io/api/?q=${encodeURIComponent(address)}&limit=5&lang=en`;
      if (countryCode) {
        url += `&osm_tag=place`;
      }
      const response = await fetch(url, { headers: { "Accept-Language": "en" } });
      if (!response.ok) return null;
      const data = await response.json();
      if (data && data.features && data.features.length > 0) {
        // Photon returns GeoJSON: coordinates are [lon, lat]
        const [lon, lat] = data.features[0].geometry.coordinates;
        return { lat: String(lat), lon: String(lon) };
      }
    } catch (_) {}
    return null;
  };

  // Geocode address to get coordinates
  const handleGeocodeAddress = async () => {
    if (!values.address || values.address.trim() === "") {
      toast.error("Please enter an address first");
      return;
    }

    setIsGeocoding(true);
    try {
      const countryCode = getCountryCode(values.country);
      const address = values.address.trim();

      // 1. Try Nominatim with the full address
      let result = await tryNominatim(address, countryCode);

      // 2. If Nominatim fails, try Photon (better for UK building-name addresses)
      if (!result) {
        result = await tryPhoton(address, countryCode);
      }

      // 3. If both fail and the address contains a UK postcode, try with just the postcode
      if (!result) {
        const postcode = extractUKPostcode(address);
        if (postcode) {
          result = await tryNominatim(postcode, countryCode);
          if (!result) {
            result = await tryPhoton(postcode, countryCode);
          }
        }
      }

      if (result) {
        setFieldValue("lat", result.lat);
        setFieldValue("lng", result.lon);
        toast.success("Coordinates found successfully!");
      } else {
        toast.warning(
          "Could not find coordinates for the address. Please enter them manually or try a more specific address."
        );
      }
    } catch (error: any) {
      console.error("Geocoding error:", error);
      toast.error("Failed to geocode address. Please enter coordinates manually.");
    } finally {
      setIsGeocoding(false);
    }
  };

  // Get current location from browser
  const handleUseCurrentLocation = () => {
    if (!navigator.geolocation) {
      toast.error("Geolocation is not supported by your browser");
      return;
    }

    toast.info("Getting your current location...");
    navigator.geolocation.getCurrentPosition(
      (position) => {
        setFieldValue("lat", position.coords.latitude.toString());
        setFieldValue("lng", position.coords.longitude.toString());
        toast.success("Current location set successfully!");
      },
      (error) => {
        console.error("Geolocation error:", error);
        toast.error("Could not get your current location. Please check your browser permissions.");
      }
    );
  };

  if (!isOpen) {
    return null;
  } else {
    return (
      <Fragment>
        <div
          style={{ backgroundColor: "#0006" }}
          className="modal fade common-modal addkeyhost-modal show d-block"
          id="addKeyHost"
          tabIndex={-1}
          aria-labelledby="addKeyHostLabel"
          aria-hidden="true"
        >
          <div className="modal-dialog modal-dialog-centered">
            <div className="modal-content">
              <div className="modal-body">
                <button
                  type="button"
                  className="btn-close"
                  onClick={handleModalClose}
                />
                <div className="common-box-body">
                  <h2>{modalTitle}</h2>
                  <div className="common-modal-form">
                    
                    {/* SECTION 1: Store Information */}
                    <div className="form-section mb-4" style={{ backgroundColor: "#f8f9fa", padding: "20px", borderRadius: "8px", border: "2px solid #5aa8a4" }}>
                      <h5 className="section-title mb-3" style={{ color: "#5aa8a4", fontWeight: "600", borderBottom: "2px solid #5aa8a4", paddingBottom: "8px" }}>
                        <i className="fa-solid fa-store me-2"></i>
                        Store Information
                      </h5>
                      <div className="row">
                        <div className="col-lg-6">
                          <div className="common-modal-field">
                            <label>
                              Store Name <span style={{ color: "red" }}>*</span>
                            </label>
                            <input
                              type="text"
                              name="store_name"
                              value={values.store_name}
                              onChange={handleChange}
                              onBlur={handleBlur}
                              className="form-control"
                              placeholder="e.g., Downtown Key Shop"
                            />
                            {touched.store_name && errors.store_name && (
                              <p className="error">{errors.store_name}</p>
                            )}
                          </div>
                        </div>
                        <div className="col-lg-6">
                          <div className="common-modal-field">
                            <label>
                              Country <span style={{ color: "red" }}>*</span>
                            </label>
                            <div className="form-control">
                              <SortedCountryDropdown
                                name="country"
                                onBlur={handleBlur}
                                value={values.country}
                                onChange={handleselectCountry}
                              />
                            </div>
                            {touched.country && errors.country && (
                              <p className="error">{errors.country}</p>
                            )}
                          </div>
                        </div>
                        <div className="col-lg-12">
                          <div className="common-modal-field">
                            <label>
                              Address <span style={{ color: "red" }}>*</span>
                            </label>
                            <input
                              type="text"
                              name="address"
                              value={values.address}
                              onChange={handleChange}
                              onBlur={handleBlur}
                              className="form-control"
                              placeholder="e.g., 123 Main St, City, State"
                            />
                            {touched.address && errors.address && (
                              <p className="error">{errors.address}</p>
                            )}
                          </div>
                        </div>
                        <div className="col-lg-12">
                          <div className="common-modal-field">
                            <label>
                              Store Information <span style={{ color: "red" }}>*</span>
                            </label>
                            <textarea
                              name="store_info"
                              value={values.store_info}
                              onChange={handleChange}
                              onBlur={handleBlur}
                              className="form-control"
                              rows={3}
                              placeholder="Describe your store, services, and any special instructions for key owners..."
                            />
                            <small className="form-text text-muted">
                              This information will be visible to key owners
                            </small>
                            {touched.store_info && errors.store_info && (
                              <p className="error">{errors.store_info}</p>
                            )}
                          </div>
                        </div>
                      </div>
                    </div>

                    {/* SECTION 2: Initial User Account */}
                    <div className="form-section mb-4" style={{ backgroundColor: "#fff3cd", padding: "20px", borderRadius: "8px", border: "2px solid #ffc107" }}>
                      <h5 className="section-title mb-3" style={{ color: "#856404", fontWeight: "600", borderBottom: "2px solid #ffc107", paddingBottom: "8px" }}>
                        <i className="fa-solid fa-user-plus me-2"></i>
                        Initial User Account
                      </h5>
                      <div className="row">
                        <div className="col-lg-12">
                          <div className="alert alert-warning" style={{ backgroundColor: "#fff3cd", border: "1px solid #ffc107", borderRadius: "4px", padding: "12px", marginBottom: "15px" }}>
                            <strong><i className="fa-solid fa-info-circle me-2"></i>Note:</strong> Create the first user account for this key host store. Login credentials (Login ID & Password) will be automatically generated and emailed. Additional users can be added later from the key host details page.
                          </div>
                        </div>
                        <div className="col-lg-6">
                          <div className="common-modal-field">
                            <label>
                              User Name <span style={{ color: "red" }}>*</span>
                            </label>
                            <input
                              value={values.username}
                              name="username"
                              onChange={handleChange}
                              onBlur={handleBlur}
                              type="text"
                              className="form-control"
                              placeholder="e.g., John Smith"
                            />
                            <small className="form-text text-muted">
                              Full name of the first user for this store
                            </small>
                            {touched.username && errors.username && (
                              <p className="error">{errors.username}</p>
                            )}
                          </div>
                        </div>
                        <div className="col-lg-6">
                          <div className="common-modal-field">
                            <label>
                              User Email <span style={{ color: "red" }}>*</span>
                            </label>
                            <input
                              type="email"
                              name="email"
                              value={values.email}
                              onChange={handleChange}
                              onBlur={handleBlur}
                              className="form-control"
                              placeholder="e.g., john.smith@example.com"
                            />
                            <small className="form-text text-muted">
                              Login credentials will be sent to this email
                            </small>
                            {touched.email && errors.email && (
                              <p className="error">{errors.email}</p>
                            )}
                          </div>
                        </div>
                      </div>
                    </div>

                    {/* SECTION 3: Contact Information */}
                    <div className="form-section mb-4">
                      <h5 className="section-title mb-3" style={{ color: "#5aa8a4", fontWeight: "600", borderBottom: "2px solid #5aa8a4", paddingBottom: "8px" }}>
                        <i className="fa-solid fa-phone me-2"></i>
                        Contact Information
                      </h5>
                      <div className="row">
                        <div className="col-lg-6">
                          <div className="common-modal-field">
                            <label>
                              Country Code <span style={{ color: "red" }}>*</span>
                            </label>
                            <CountrySelect
                              className="form-select"
                              name="countryCode"
                              labels={en}
                              value={country}
                              onChange={handleCountryChange}
                              onblur={handleBlur}
                            />
                            {touched.countryCode && errors.countryCode && (
                              <p className="error">{errors.countryCode}</p>
                            )}
                          </div>
                        </div>
                        <div className="col-lg-6">
                          <div className="common-modal-field">
                            <label>
                              Phone Number <span style={{ color: "red" }}>*</span>
                            </label>
                            <input
                              className="phone-input form-control"
                              type="tel"
                              placeholder="1234567890"
                              minLength={8}
                              maxLength={10}
                              name="phoneNumber"
                              onChange={handleChange}
                              value={values.phoneNumber}
                              onBlur={handleBlur}
                            />
                            {touched.phoneNumber && errors.phoneNumber && (
                              <p className="error">{errors.phoneNumber}</p>
                            )}
                          </div>
                        </div>
                      </div>
                    </div>

                    {/* SECTION 4: Location Coordinates */}
                    <div className="form-section mb-4">
                      <h5 className="section-title mb-3" style={{ color: "#5aa8a4", fontWeight: "600", borderBottom: "2px solid #5aa8a4", paddingBottom: "8px" }}>
                        Location Coordinates <span style={{ color: "red" }}>*</span>
                      </h5>
                      
                      {/* Quick Action Buttons */}
                      <div className="row mb-3">
                        <div className="col-12">
                          <div className="d-flex gap-2 flex-wrap">
                            <button
                              type="button"
                              className="btn btn-outline-primary btn-sm"
                              onClick={handleGeocodeAddress}
                              disabled={isGeocoding || !values.address}
                              title="Convert the address above to coordinates"
                            >
                              {isGeocoding ? (
                                <>
                                  <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                                  Finding coordinates...
                                </>
                              ) : (
                                <>
                                  <i className="fa-solid fa-map-location-dot me-2"></i>
                                  Get Coordinates from Address
                                </>
                              )}
                            </button>
                            <button
                              type="button"
                              className="btn btn-outline-success btn-sm"
                              onClick={handleUseCurrentLocation}
                              title="Use your current location"
                            >
                              <i className="fa-solid fa-location-crosshairs me-2"></i>
                              Use My Current Location
                            </button>
                          </div>
                          <small className="form-text text-muted d-block mt-2">
                            💡 <strong>Easy Options:</strong> Click "Get Coordinates from Address" to automatically find coordinates, or use your current location if you're at the store.
                          </small>
                        </div>
                      </div>

                      {/* Coordinate Input Fields */}
                      <div className="row">
                        <div className="col-lg-6">
                          <div className="common-modal-field">
                            <label>
                              Latitude <span style={{ color: "red" }}>*</span>
                            </label>
                            <input
                              name="lat"
                              value={values.lat}
                              onChange={handleChange}
                              onBlur={handleBlur}
                              type="number"
                              step="any"
                              className="form-control"
                              placeholder="e.g., 37.7749"
                            />
                            <small className="form-text text-muted">
                              Range: -90 to 90
                            </small>
                            {touched.lat && errors.lat && (
                              <p className="error">{errors.lat}</p>
                            )}
                          </div>
                        </div>
                        <div className="col-lg-6">
                          <div className="common-modal-field">
                            <label>
                              Longitude <span style={{ color: "red" }}>*</span>
                            </label>
                            <input
                              name="lng"
                              value={values.lng}
                              onChange={handleChange}
                              onBlur={handleBlur}
                              type="number"
                              step="any"
                              className="form-control"
                              placeholder="e.g., -122.4194"
                            />
                            <small className="form-text text-muted">
                              Range: -180 to 180
                            </small>
                            {touched.lng && errors.lng && (
                              <p className="error">{errors.lng}</p>
                            )}
                          </div>
                        </div>
                      </div>

                      {/* Preview Link */}
                      {values.lat && values.lng && (
                        <div className="row mt-2">
                          <div className="col-12">
                            <div className="alert alert-success" style={{ backgroundColor: "#d4edda", border: "1px solid #c3e6cb", borderRadius: "4px", padding: "10px" }}>
                              <i className="fa-solid fa-check-circle me-2"></i>
                              <strong>Coordinates set!</strong>
                              <a
                                href={`https://www.google.com/maps?q=${values.lat},${values.lng}`}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="ms-2"
                                style={{ color: "#155724", textDecoration: "underline" }}
                              >
                                View on Google Maps <i className="fa-solid fa-external-link-alt ms-1"></i>
                              </a>
                            </div>
                          </div>
                        </div>
                      )}
                    </div>

                    {/* SECTION 5: Operating Hours */}
                    <div className="form-section mb-4">
                      <h5 className="section-title mb-3" style={{ color: "#5aa8a4", fontWeight: "600", borderBottom: "2px solid #5aa8a4", paddingBottom: "8px" }}>
                        Operating Hours <span style={{ color: "red" }}>*</span>
                      </h5>

                      {/* ── Quick Presets ── */}
                      <div className="mb-3">
                        <span style={{ fontSize: "11px", fontWeight: "700", color: "#6c757d", textTransform: "uppercase", letterSpacing: "0.7px" }}>
                          ⚡ Quick Presets
                        </span>
                        <div className="d-flex flex-wrap gap-2 mt-2">
                          {[
                            { label: "Mon–Fri  9:00–17:00", key: "business" },
                            { label: "Mon–Sat  9:00–18:00", key: "extended" },
                            { label: "7 Days  9:00–17:00",  key: "7days"    },
                            { label: "24 / 7 Always Open",  key: "247"      },
                          ].map((p) => (
                            <button
                              key={p.key}
                              type="button"
                              className="btn btn-outline-secondary btn-sm"
                              onClick={() => applyPreset(p.key)}
                              style={{ borderRadius: "20px", fontSize: "12px", padding: "4px 14px" }}
                            >
                              {p.label}
                            </button>
                          ))}
                        </div>
                      </div>

                      {/* ── Bulk Apply Bar ── */}
                      <div
                        className="mb-3 px-3 py-2"
                        style={{ backgroundColor: "#f0fafa", borderRadius: "8px", border: "1px dashed #5aa8a4" }}
                      >
                        <span style={{ fontSize: "11px", fontWeight: "700", color: "#6c757d", textTransform: "uppercase", letterSpacing: "0.7px" }}>
                          🔁 Bulk Apply — set a time range and push it to multiple days at once
                        </span>
                        <div className="d-flex flex-wrap gap-2 align-items-end mt-2">
                          <div>
                            <label style={{ fontSize: "11px", color: "#555", display: "block", marginBottom: "2px" }}>Opens</label>
                            <input
                              type="time"
                              className="form-control form-control-sm"
                              value={bulkStart}
                              onChange={(e) => setBulkStart(e.target.value)}
                              style={{ width: "130px" }}
                            />
                          </div>
                          <div>
                            <label style={{ fontSize: "11px", color: "#555", display: "block", marginBottom: "2px" }}>Closes</label>
                            <input
                              type="time"
                              className="form-control form-control-sm"
                              value={bulkEnd}
                              onChange={(e) => setBulkEnd(e.target.value)}
                              style={{ width: "130px" }}
                            />
                          </div>
                          <button
                            type="button"
                            className="btn btn-sm btn-primary"
                            onClick={() => applyBulkTime([0, 1, 2, 3, 4, 5, 6])}
                            style={{ fontSize: "12px" }}
                          >
                            All Days
                          </button>
                          <button
                            type="button"
                            className="btn btn-sm btn-outline-primary"
                            onClick={() => applyBulkTime([0, 1, 2, 3, 4])}
                            style={{ fontSize: "12px" }}
                          >
                            Weekdays
                          </button>
                          <button
                            type="button"
                            className="btn btn-sm btn-outline-primary"
                            onClick={() => applyBulkTime([5, 6])}
                            style={{ fontSize: "12px" }}
                          >
                            Weekend
                          </button>
                        </div>
                      </div>

                      {/* ── Day-by-day grid ── */}
                      <div className="common-modal-field" onBlur={handleBlur}>

                        {/* Column headers */}
                        <div
                          style={{
                            display: "grid",
                            gridTemplateColumns: "110px 1fr 1fr 90px 36px",
                            gap: "8px",
                            padding: "0 10px 6px",
                            fontWeight: "700",
                            fontSize: "11px",
                            color: "#5aa8a4",
                            textTransform: "uppercase",
                            letterSpacing: "0.5px",
                          }}
                        >
                          <span>Day</span>
                          <span>Opens</span>
                          <span>Closes</span>
                          <span style={{ textAlign: "center" }}>Status</span>
                          <span />
                        </div>

                        {/* Weekdays group */}
                        <div style={{ fontSize: "10px", fontWeight: "700", color: "#aaa", textTransform: "uppercase", letterSpacing: "1px", padding: "4px 10px" }}>
                          Weekdays
                        </div>
                        {timeSlots?.slice(0, 5).map((slot: TimeSlot, localIdx: number) => {
                          const index = localIdx;
                          const dayIsOpen = !slot.closed;
                          return (
                            <div
                              key={index}
                              style={{
                                display: "grid",
                                gridTemplateColumns: "110px 1fr 1fr 90px 36px",
                                gap: "8px",
                                alignItems: "center",
                                padding: "8px 10px",
                                marginBottom: "4px",
                                borderRadius: "6px",
                                backgroundColor: dayIsOpen ? (index % 2 === 0 ? "#f8f9fa" : "#fff") : "#f0f0f0",
                                opacity: dayIsOpen ? 1 : 0.6,
                                transition: "opacity 0.2s, background-color 0.2s",
                              }}
                            >
                              <span style={{ fontWeight: "600", fontSize: "14px", color: dayIsOpen ? "#333" : "#999" }}>
                                {slot.day}
                              </span>
                              <input
                                type="time"
                                className="form-control form-control-sm"
                                disabled={!dayIsOpen}
                                value={slot.start_time || ""}
                                onChange={(e) => updateTimeSlot(index, "start_time", e.target.value)}
                                style={{ cursor: dayIsOpen ? "pointer" : "not-allowed" }}
                              />
                              <input
                                type="time"
                                className="form-control form-control-sm"
                                disabled={!dayIsOpen}
                                value={slot.end_time || ""}
                                onChange={(e) => updateTimeSlot(index, "end_time", e.target.value)}
                                style={{ cursor: dayIsOpen ? "pointer" : "not-allowed" }}
                              />
                              <div className="form-check form-switch d-flex align-items-center justify-content-center m-0 gap-1">
                                <input
                                  className="form-check-input m-0"
                                  type="checkbox"
                                  id={`open-toggle-${index}`}
                                  checked={dayIsOpen}
                                  onChange={(e) => updateTimeSlot(index, "closed", !e.target.checked)}
                                  style={{ width: "2.2em", height: "1.15em", cursor: "pointer" }}
                                />
                                <label
                                  className="form-check-label"
                                  htmlFor={`open-toggle-${index}`}
                                  style={{
                                    fontSize: "11px",
                                    fontWeight: "600",
                                    color: dayIsOpen ? "#198754" : "#6c757d",
                                    cursor: "pointer",
                                    userSelect: "none",
                                    minWidth: "40px",
                                  }}
                                >
                                  {dayIsOpen ? "Open" : "Closed"}
                                </label>
                              </div>
                              <button
                                type="button"
                                className="btn btn-sm btn-outline-secondary p-0 d-flex align-items-center justify-content-center"
                                title={`Copy ${slot.day}'s hours to all other open days`}
                                disabled={!dayIsOpen || !slot.start_time || !slot.end_time}
                                onClick={() => copyRowToAll(index)}
                                style={{ width: "30px", height: "30px", fontSize: "12px" }}
                              >
                                <i className="fa-solid fa-copy" />
                              </button>
                            </div>
                          );
                        })}

                        {/* Weekend group */}
                        <div style={{ fontSize: "10px", fontWeight: "700", color: "#aaa", textTransform: "uppercase", letterSpacing: "1px", padding: "8px 10px 4px" }}>
                          Weekend
                        </div>
                        {timeSlots?.slice(5).map((slot: TimeSlot, localIdx: number) => {
                          const index = 5 + localIdx;
                          const dayIsOpen = !slot.closed;
                          return (
                            <div
                              key={index}
                              style={{
                                display: "grid",
                                gridTemplateColumns: "110px 1fr 1fr 90px 36px",
                                gap: "8px",
                                alignItems: "center",
                                padding: "8px 10px",
                                marginBottom: "4px",
                                borderRadius: "6px",
                                backgroundColor: dayIsOpen ? (index % 2 === 0 ? "#f8f9fa" : "#fff") : "#f0f0f0",
                                opacity: dayIsOpen ? 1 : 0.6,
                                transition: "opacity 0.2s, background-color 0.2s",
                              }}
                            >
                              <span style={{ fontWeight: "600", fontSize: "14px", color: dayIsOpen ? "#333" : "#999" }}>
                                {slot.day}
                              </span>
                              <input
                                type="time"
                                className="form-control form-control-sm"
                                disabled={!dayIsOpen}
                                value={slot.start_time || ""}
                                onChange={(e) => updateTimeSlot(index, "start_time", e.target.value)}
                                style={{ cursor: dayIsOpen ? "pointer" : "not-allowed" }}
                              />
                              <input
                                type="time"
                                className="form-control form-control-sm"
                                disabled={!dayIsOpen}
                                value={slot.end_time || ""}
                                onChange={(e) => updateTimeSlot(index, "end_time", e.target.value)}
                                style={{ cursor: dayIsOpen ? "pointer" : "not-allowed" }}
                              />
                              <div className="form-check form-switch d-flex align-items-center justify-content-center m-0 gap-1">
                                <input
                                  className="form-check-input m-0"
                                  type="checkbox"
                                  id={`open-toggle-${index}`}
                                  checked={dayIsOpen}
                                  onChange={(e) => updateTimeSlot(index, "closed", !e.target.checked)}
                                  style={{ width: "2.2em", height: "1.15em", cursor: "pointer" }}
                                />
                                <label
                                  className="form-check-label"
                                  htmlFor={`open-toggle-${index}`}
                                  style={{
                                    fontSize: "11px",
                                    fontWeight: "600",
                                    color: dayIsOpen ? "#198754" : "#6c757d",
                                    cursor: "pointer",
                                    userSelect: "none",
                                    minWidth: "40px",
                                  }}
                                >
                                  {dayIsOpen ? "Open" : "Closed"}
                                </label>
                              </div>
                              <button
                                type="button"
                                className="btn btn-sm btn-outline-secondary p-0 d-flex align-items-center justify-content-center"
                                title={`Copy ${slot.day}'s hours to all other open days`}
                                disabled={!dayIsOpen || !slot.start_time || !slot.end_time}
                                onClick={() => copyRowToAll(index)}
                                style={{ width: "30px", height: "30px", fontSize: "12px" }}
                              >
                                <i className="fa-solid fa-copy" />
                              </button>
                            </div>
                          );
                        })}

                        {timeError === true ? (
                          <p className="error" style={{ marginTop: "8px" }}>{errorMessage}</p>
                        ) : null}
                      </div>
                    </div>

                    {/* SECTION 6: Box Assignment */}
                    <div className="form-section mb-4">
                      <h5 className="section-title mb-3" style={{ color: "#5aa8a4", fontWeight: "600", borderBottom: "2px solid #5aa8a4", paddingBottom: "8px" }}>
                        Box Assignment <span style={{ color: "#6c757d", fontSize: "14px", fontWeight: "normal" }}>(Optional)</span>
                      </h5>
                      <div className="row">
                        <div className="col-12">
                          <div className="common-modal-field">
                            <label>Select Boxes</label>
                            {BoxItems && BoxItems.length > 0 ? (
                              <>
                                <Multiselect
                                  placeholder="Choose boxes (optional)"
                                  className="multisel form-select"
                                  displayValue="boxId"
                                  showCheckbox
                                  emptyRecordMsg="No boxes available"
                                  selectedValues={selectedOptions || []}
                                  onSelect={onSelected}
                                  onRemove={onRemove}
                                  options={BoxItems.filter((box: any) => box && box.boxId) || []}
                                  style={{
                                    searchBox: {
                                      minHeight: "38px",
                                      padding: "5px"
                                    }
                                  }}
                                />
                                <small className="form-text text-muted">
                                  You can assign boxes now or later. Boxes can be managed after creating the key host.
                                </small>
                              </>
                            ) : (
                              <div className="alert alert-info" style={{ backgroundColor: "#e7f6f5", border: "1px solid #5aa8a4", borderRadius: "4px", padding: "12px", marginTop: "8px" }}>
                                <strong>ℹ️ No boxes available</strong>
                                <p className="mb-0 mt-1">You can create the key host now and assign boxes later when they become available.</p>
                              </div>
                            )}
                            {touched.selectedOption && errors.selectedOption && (
                              <p className="error">{errors.selectedOption}</p>
                            )}
                          </div>
                        </div>
                      </div>
                    </div>

                  </div>
                  <div className="common-modal-btns">
                    <button
                      type="button"
                      className="btn primary-btn"
                      onClick={(e) => {
                        handleSubmit(e);
                        handleErrorClick();
                      }}
                    >
                      Submit
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </Fragment>
    );
  }
};

export default KeyHostModal;
